// exports Bounds, bezierBounds, segmentBounds, BoundsBuilder, default: Bounds

import { Base } from 'gear';

import { Point }  from './point';
import { Matrix } from './matrix';

import { Xml } from '../util';

const _Math = Math,
      _Math_max = _Math.max,
      _Math_min = _Math.min,
      _abs      = _Math.abs,
      _floor    = _Math.floor,
      _parseFloat = parseFloat;

export const Bounds = function(lx, ly, hx, hy) {
  const _this = this;
  if( ! (_this instanceof Bounds )) {
    return new Bounds(lx,ly,hx,hy);
  }
  if( ly === undefined ) {
    const o = lx;
    _this.lx = o.lx;
    _this.ly = o.ly;
    _this.hx = o.hx;
    _this.hy = o.hy;
  }
  else {
    _this.lx = lx;
    _this.ly = ly;
    _this.hx = hx;
    _this.hy = hy;
  }
};

Base.Type['Bounds'] = Bounds;

export default Bounds;

const _toFixed = function(x) {
  return +x.toFixed(4);
};

const _textualize = function() {
  const _this = this, sep = ' ';
  return _toFixed(_this.lx) + sep +
         _toFixed(_this.ly) + sep +
         _toFixed(_this.hx) + sep +
         _toFixed(_this.hy);
};

const _isNear = function( other, eps = 0.001 ) {
  const _this = this;
  return _abs(_this.lx-other.lx) < eps &&
         _abs(_this.ly-other.ly) < eps &&
         _abs(_this.hx-other.hx) < eps &&
         _abs(_this.hy-other.hy) < eps;
};

Bounds.prototype = {
  constructor: Bounds,
  type: 'Bounds',
  clone() {
    return new Bounds(this);
  },
  isEqual: _isNear,
  isNear: _isNear,
  translated( x, y ) {
    return this.clone().translate(x,y);
  },
  translate( x, y ) { if( y===undefined ) { y = x.y; x = x.x; }
    const _this=this;
    _this.lx += x; _this.ly += y;
    _this.hx += x; _this.hy += y;
    return this;
  },
  scaled( sx, sy ) {
    return this.clone().scale(sx,sy);
  },
  scale( sx, sy ) { if( sy===undefined ) { sy = sx; }
    this.scalingTransform( Matrix.scaleFrom(this.center,sx,sy) );
    return this;
  },
  scalingTransform( t ) {
    const _this = this,
        P0 = t.apply( _this.lx_ly ), P0_x = P0.x, P0_y = P0.y,
        P1 = t.apply( _this.hx_hy ), P1_x = P1.x, P1_y = P1.y;
    _this.lx = P0_x < P1_x ? P0_x : P1_x;
    _this.ly = P0_y < P1_y ? P0_y : P1_y;
    _this.hx = P0_x > P1_x ? P0_x : P1_x;
    _this.hy = P0_y > P1_y ? P0_y : P1_y;
    return this;
  },
  scalingTransformed( t ) {
    return this.clone().scalingTransform(t);
  },
  deinterpolate( point ) {
    const _this = this, _this_lx = _this.lx, _this_ly = _this.ly;
    return { x: ( point.x - _this_lx ) / ( _this.hx - _this_lx ),
             y: ( point.y - _this_ly ) / ( _this.hy - _this_ly ) };
  },
  interpolate( x, y ) { if(y===undefined) { const p=x; x=p.x; y=p.y; }
    const iv = new Point((1 - x) * this.lx + x * this.hx, (1 - y) * this.ly + y * this.hy);
    return iv;
  },

  get center() {
    return new Point(0.5 * (this.lx + this.hx), 0.5 * (this.ly + this.hy));
  },

  get lx_ly() {
    return new Point(this.lx,this.ly);
  },
  get lx_hy() {
    return new Point(this.lx,this.hy);
  },
  get hx_ly() {
    return new Point(this.hx,this.ly);
  },
  get hx_hy() {
    return new Point(this.hx,this.hy);
  },

  get mx() {
    return ( this.lx + this.hx ) / 2;
  },
  get my() {
    return ( this.ly + this.hy ) / 2;
  },

  contains( x, y ) { if(y===undefined) { const p=x; x=p.x; y=p.y; }
    return x >= this.lx && x <= this.hx && y >= this.ly && y <= this.hy;
  },
  inflated( dw, dh ) {
    return this.clone().inflate(dw,dh);
  },
  inflate( dw, dh = dw ) {
    this.lx -= dw;
    this.hx += dw;
    this.ly -= dh;
    this.hy += dh;
    return this;
  },

  get area() {
    return this.width * this.height;
  },

  get width() {
    return this.hx - this.lx;
  },

  set width( v ) {
    const w = this.width,
          d = (v-w)/2;
    this.lx -= d;
    this.hx += d;
  },

  get height() {
    return this.hy - this.ly;
  },
  set height( v ) {
    const h = this.height,
          d = (v-h)/2;
    this.ly -= d;
    this.hy += d;
  },

  toXmlString: _textualize,
  textualize: _textualize,

  draw( context ) {
    const lx = _floor(this.lx)+0.5, ly = _floor(this.ly)+0.5, hx = _floor(this.hx)+0.5, hy = _floor(this.hy)+0.5;
    context.beginPath();
    context.moveTo(lx,ly);
    context.lineTo(lx,hy);
    context.lineTo(hx,hy);
    context.lineTo(hx,ly);
    context.lineTo(lx,ly);
    context.closePath();
  },

  setXmlAttr( node, tag ) {
    node.setAttribute(tag,this.toXmlString());
  }
};

Bounds.around = function( center, width, height ) {
  return new Bounds(center.x-width/2,center.y-height/2,center.x+width/2,center.y+height/2);
};

Bounds.fromBox = function( box ) {
  return new Bounds(box.x, box.y, box.x + box.width, box.y + box.height);
};

Bounds.fromXmlString = function( s ) {
  const a = s.split(' ');
  return new Bounds(_parseFloat(a[0]), _parseFloat(a[1]), _parseFloat(a[2]), _parseFloat(a[3]));
};

// Unrolled performant version
Bounds.ofPoints = function( points ) {
  if( points.length === 0 ) {
    return null;
  }
  let p = points[0],
      lx = p.x, ly = p.y,
      hx = lx , hy = ly;
  for(let k=1,kEnd=points.length;k<kEnd;++k) {
    p = points[k];
    const x = p.x, y = p.y;
         if(lx > x) { lx = x; }
    else if(hx < x) { hx = x; }
         if(ly > y) { ly = y; }
    else if(hy < y) { hy = y; }
  }
  return new Bounds(lx,ly,hx,hy);
};

// Unrolled performant version
Bounds.ofPointsAfter = function( points, t ) {
  if( points.length === 0 ) {
    return null;
  }

  // [Inline:Matrix.apply]
  // const (tpx,tpy) = t.apply( points[0] );
  let p = points[0],
      px = p.x, py = p.y,
      tpx = px * t.s00 + py * t.s01 + t.dx,
      tpy = px * t.s10 + py * t.s11 + t.dy;
  // ---

  let lx = tpx, ly = tpy,
      hx = tpx, hy = tpy;
  for(let k=1,kEnd=points.length;k<kEnd;++k) {

    // [Inline:Matrix.apply]
    // (tpx,tpy) = t.apply( points[k] );
    p = points[k];
    px = p.x; py = p.y;
    tpx = px * t.s00 + py * t.s01 + t.dx;
    tpy = px * t.s10 + py * t.s11 + t.dy;
    // ---

         if(lx > tpx) { lx = tpx; }
    else if(hx < tpx) { hx = tpx; }

         if(ly > tpy) { ly = tpy; }
    else if(hy < tpy) { hy = tpy; }
  }
  return new Bounds(lx,ly,hx,hy);
};

// Unrolled performant version
Bounds.ofThings = function( things ) {
  const count = things.length;
  if( count === 0 ) {
    return null;
  }
  let b = things[0].bounds;
  if( count === 1 ) {
    return b;
  }

  let lx = b.lx, ly = b.ly,
      hx = b.hx, hy = b.hy;
  for(let k=1;k<count;++k) {
    b = things[k].bounds;
    if(lx > b.lx) { lx = b.lx; }
    if(ly > b.ly) { ly = b.ly; }
    if(hx < b.hx) { hx = b.hx; }
    if(hy < b.hy) { hy = b.hy; }
  }
  return new Bounds(lx,ly,hx,hy);
};

// Unrolled performant version
Bounds.ofThingsAfter = function( things, transform ) {
  const count = things.length;
  if( count === 0 ) {
    return null;
  }
  let b = things[0].boundsAfter(transform);
  if( count === 1 ) {
    return b;
  }

  let lx = b.lx, ly = b.ly,
      hx = b.hx, hy = b.hy;
  for(let k=1;k<count;++k) {
    b = things[k].boundsAfter(transform);
    if(lx > b.lx) { lx = b.lx; }
    if(ly > b.ly) { ly = b.ly; }
    if(hx < b.hx) { hx = b.hx; }
    if(hy < b.hy) { hy = b.hy; }
  }
  return new Bounds(lx,ly,hx,hy);
};

// Returns true iff the intervals [al,ar] and [bl,br] have more than one
// point in common. For a function which also returns true when the
// intervals overlap in one point, it suffices to replace the two <'s for
// <='s. Precondition: al<=ar && bl<=br.
const _intervalsOverlap = function(al,ar,bl,br){
  return (al<=bl)?(bl<ar):(al<br);
};

// Returns true iff the intervals overlap in x and in y. Technically
// speaking, the function returns true iff the intersection set has
// cardinality \aleph_2.
Bounds.areOverlapping = function( ba, bb ) {
  return _intervalsOverlap(ba.lx,ba.hx,bb.lx,bb.hx) &&
         _intervalsOverlap(ba.ly,ba.hy,bb.ly,bb.hy);
};

Bounds.intersection = function( ba, bb ) {
  if(Bounds.areOverlapping(ba,bb)){
    return new Bounds(_Math_max(ba.lx, bb.lx),
                      _Math_max(ba.ly, bb.ly),
                      _Math_min(ba.hx, bb.hx),
                      _Math_min(ba.hy, bb.hy));
  }else{
    return null;
  }
};

Bounds.union = function( boundsList ) {
  const ma = Number.MAX_VALUE;
  const result = { lx: ma, ly: ma, hx: -ma, hy: -ma };

  for(let k=0,kEnd=boundsList.length;k<kEnd;++k) {
    const bounds=boundsList[k];
  //_.forEach(boundsList,function(bounds) {
    if(bounds.lx < result.lx) { result.lx = bounds.lx; }
    if(bounds.ly < result.ly) { result.ly = bounds.ly; }
    if(bounds.hx > result.hx) { result.hx = bounds.hx; }
    if(bounds.hy > result.hy) { result.hy = bounds.hy; }
  }//);
  return ( result.lx < ma && result.ly < ma && result.hx > -ma && result.hy > -ma ) ?
         new Bounds(result)
       : new Bounds(0,0,1000,1000);
};

Bounds.overlapPercentage = function( ba, bb ) {
  const bi = Bounds.intersection(ba, bb);
  if( bi ) {
    const ba_area = ba.area, bb_area = bb.area, bi_area = bi.area;
    return bi_area / _Math_min(ba_area, bb_area);
  }
  return 0;
};

Bounds.horizontalOverlapPercentage = function( ba, bb ) {
  const bi = Bounds.intersection(ba, bb);
  if( bi ) {
    const ba_width = ba.width, bb_width = bb.width, bi_width = bi.width;
    return bi_width / _Math_min(ba_width, bb_width);
  }
  return 0;
};

const _alignTo = function( align, where ) {
  return align.indexOf(where) !== -1;
};

Bounds.alignOffset = function( bounds, alignment, toBounds, margin ) {
  const m = margin || 0;
  const toCenter = toBounds.center;
  const center   = bounds.center;
  const align    = alignment.toLowerCase();

  return new Point(

    // delta x
    _alignTo(align,'left'  ) ? toBounds.lx+m - bounds.lx  :
    _alignTo(align,'center') ? toCenter.x - center.x      :
    _alignTo(align,'right' ) ? toBounds.hx-m - bounds.hx  : 0,

    // delta y
    _alignTo(align,'bottom') ? toBounds.ly+m - bounds.ly  :
    _alignTo(align,'middle') ? toCenter.y - center.y      :
    _alignTo(align,'top'   ) ? toBounds.hy-m - bounds.hy  : 0
  );
};

// Raphael.bezierBBox from https://raw.githubusercontent.com/DmitryBaranovskiy/raphael/master/raphael.js
const abs = _abs, sqrt = Math.sqrt, pow = Math.pow, mmin = _Math_min, mmax = _Math_max;
const findDotAtSegment = function( p1x, p1y, c1x, c1y, c2x, c2y, p2x, p2y, t ) {
  const t1 = 1 - t;
  return {
    x: pow(t1, 3) * p1x + pow(t1, 2) * 3 * t * c1x + t1 * 3 * t * t * c2x + pow(t, 3) * p2x,
    y: pow(t1, 3) * p1y + pow(t1, 2) * 3 * t * c1y + t1 * 3 * t * t * c2y + pow(t, 3) * p2y
  };
};
const curveDim = function( p1x, p1y, c1x, c1y, c2x, c2y, p2x, p2y ) {
  let a = (c2x - 2 * c1x + p1x) - (p2x - 2 * c2x + c1x),
      b = 2 * (c1x - p1x) - 2 * (c2x - c1x),
      c = p1x - c1x,
      t1 = (-b + sqrt(b * b - 4 * a * c)) / 2 / a,
      t2 = (-b - sqrt(b * b - 4 * a * c)) / 2 / a,
      y = [p1y, p2y],
      x = [p1x, p2x],
      dot;
  if( abs(t1) > "1e12" ) {
    t1 = 0.5;
  }
  if( abs(t2) > "1e12" ) {
    t2 = 0.5;
  }
  if(t1 > 0 && t1 < 1) {
    dot = findDotAtSegment(p1x, p1y, c1x, c1y, c2x, c2y, p2x, p2y, t1);
    x.push(dot.x);
    y.push(dot.y);
  }
  if(t2 > 0 && t2 < 1) {
    dot = findDotAtSegment(p1x, p1y, c1x, c1y, c2x, c2y, p2x, p2y, t2);
    x.push(dot.x);
    y.push(dot.y);
  }
  a = (c2y - 2 * c1y + p1y) - (p2y - 2 * c2y + c1y);
  b = 2 * (c1y - p1y) - 2 * (c2y - c1y);
  c = p1y - c1y;
  t1 = (-b + sqrt(b * b - 4 * a * c)) / 2 / a;
  t2 = (-b - sqrt(b * b - 4 * a * c)) / 2 / a;
  if( abs(t1) > "1e12" ) { //[TODO]
    t1 = 0.5;
  }
  if( abs(t2) > "1e12" ) {
    t2 = 0.5;
  }
  if(t1 > 0 && t1 < 1) {
    dot = findDotAtSegment(p1x, p1y, c1x, c1y, c2x, c2y, p2x, p2y, t1);
    x.push(dot.x);
    y.push(dot.y);
  }
  if(t2 > 0 && t2 < 1) {
    dot = findDotAtSegment(p1x, p1y, c1x, c1y, c2x, c2y, p2x, p2y, t2);
    x.push(dot.x);
    y.push(dot.y);
  }
  return new Bounds( mmin.apply(0, x), mmin.apply(0, y),
                     mmax.apply(0, x), mmax.apply(0, y) );
};

export const bezierBounds = function( P0, P1, P2, P3 ) {
  return curveDim(P0.x, P0.y, P1.x, P1.y, P2.x, P2.y, P3.x, P3.y);
};

export const segmentBounds = function( P0, P1 ) {
  const P0_x = P0.x, P0_y = P0.y, P1_x = P1.x, P1_y = P1.y;
  return new Bounds( P0_x < P1_x ? P0_x : P1_x, P0_y < P1_y ? P0_y : P1_y, P0_x > P1_x ? P0_x : P1_x, P0_y > P1_y ? P0_y : P1_y);
};

// [TODO] Remove this abstraction
export const BoundsBuilder = function() {};
BoundsBuilder.prototype = {
  update( b) {
    let this_b = this.b;
    if( ! this.b ) { this_b = this.b = b.clone(); }
    if( this_b.lx > b.lx ) { this_b.lx = b.lx; }
    if( this_b.ly > b.ly ) { this_b.ly = b.ly; }
    if( this_b.hx < b.hx ) { this_b.hx = b.hx; }
    if( this_b.hy < b.hy ) { this_b.hy = b.hy; }
  },
  updateWithPoint( p ) {
    let this_b = this.b, p_x = p.x, p_y = p.y;
    if( ! this_b ) {
      this_b = this.b = new Bounds(p_x, p_y, p_x, p_y);
    }
    if( this_b.lx > p.x ) { this_b.lx = p.x; }
    if( this_b.ly > p.y ) { this_b.ly = p.y; }
    if( this_b.hx < p.x ) { this_b.hx = p.x; }
    if( this_b.hy < p.y ) { this_b.hy = p.y; }
  },
  get bounds() {
    return this.b;
  }
};

Bounds.fromXmlAttr = function( node, tag, def ) {
  const valueString = node.getAttribute(tag) ;
  if( valueString ) {
    return Bounds.fromXmlString(valueString);
  }
  return def;
};

Xml.Parser['Bounds'] = {
  decode( node, tag, v ) {
    return Bounds.fromXmlAttr(node,tag,v);
  },
  encode( node, tag, v ) {
    v.setXmlAttr(node,tag);
  }
};



/** WEBPACK FOOTER **
 ** ./src/cdl/geometry/bounds.js
 **/